import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { observer } from "mobx-react";
import React from "react";
import Fetch from "../api/Fetch";
import { BACKEND_URI } from "../constants";
import Toaster from "../common/Toaster";
import { GuestList, Location } from "../model/Types";

const GuestListView = observer(() => {
    const [loading, setLoading] = React.useState(true);
    const [guestLists, setGuestLists] = React.useState<any[]>([]);
    const [id, setId] = React.useState<number | undefined>(undefined);
    const [locations, setLocations] = React.useState<Location[]>([]);
    const [selectedLocation, setSelectedLocation] = React.useState<number | undefined>(undefined);
    const [search, setSearch] = React.useState("");

    const cb = React.useCallback(async () => {
        setLoading(true);
        try {
            const gl = await Fetch<GuestList[]>(`${BACKEND_URI}/guest-list`, {
                includeAuth: true,
            });
            setGuestLists(gl);
            const l = await Fetch<Location[]>(`${BACKEND_URI}/locations`, {
                includeAuth: true,
            });
            setLocations(l.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
            setSelectedLocation(l.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))[0]?.id);
        } catch (err: any) {
            console.log(err);
            Toaster.error(err.message || err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }, []);

    React.useEffect(() => {
        cb();
    }, [cb]);

    const filteredGuestLists = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return guestLists.map((gl) => ({ ...gl, totalGuests: (gl.guests || []).length }));
        return guestLists
            .map((gl) => ({
                ...gl,
                totalGuests: (gl.guests || []).length,
                guests: (gl.guests || []).filter(
                    (g: any) =>
                        (g.name || "").toLowerCase().includes(term) ||
                        (g.location?.name || "").toLowerCase().includes(term)
                ),
            }))
            .filter((gl) => gl.guests.length > 0 || (gl.title || "").toLowerCase().includes(term));
    }, [guestLists, search]);

    const logGuest = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        setLoading(true);
        try {
            await Fetch(`${BACKEND_URI}/guest-list-guest`, {
                includeAuth: true,
                body: {
                    id,
                    locationId: selectedLocation,
                },
                isText: true,
                method: "POST",
            });
            setId(undefined);
            setSelectedLocation(locations[0]?.id);
            cb();
        } catch (err: any) {
            console.log(err);
            Toaster.error(err.message || err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    return (
        <Box sx={{ marginBottom: "20px" }}>
            <Card elevation={3}>
                <CardHeader
                    title="Guest List"
                    titleTypographyProps={{ variant: "h5" }}
                    action={
                        <Button onClick={() => cb()} variant="contained" startIcon={<RefreshIcon />} size="small">
                            Refresh
                        </Button>
                    }
                    sx={{ backgroundColor: "background.paper", borderBottom: 1, borderColor: "divider" }}
                />
                <Divider />
                <Box sx={{ p: 2, backgroundColor: "background.paper" }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search guests by name or location"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                </Box>
                <Divider />
                <CardContent sx={{ p: 0 }}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ py: 4 }}>
                                            <LinearProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredGuestLists.length > 0 ? (
                                    <>
                                        {filteredGuestLists.map((gl) => (
                                            <>
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={2}
                                                        sx={{
                                                            py: 2,
                                                            backgroundColor: "action.selected",
                                                            borderLeft: 4,
                                                            borderLeftColor: "primary.main",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "text.primary",
                                                                }}
                                                            >
                                                                {gl.title}
                                                            </Typography>
                                                            <Chip
                                                                label={
                                                                    search.trim() && gl.guests.length !== gl.totalGuests
                                                                        ? `${gl.guests.length} of ${gl.totalGuests} guests`
                                                                        : `${gl.guests.length} guests`
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                {gl.guests.map((g, index) => (
                                                    <TableRow
                                                        key={g.id}
                                                        sx={{
                                                            "&:hover": { backgroundColor: "action.hover" },
                                                            backgroundColor: "background.paper",
                                                            opacity: g.fulfilledByUser ? 0.6 : 1,
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 3 }}>
                                                                <PersonIcon color={g.fulfilledByUser ? "disabled" : "action"} />
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        textDecoration: g.fulfilledByUser ? "line-through" : "none",
                                                                        fontWeight: g.fulfilledByUser ? "normal" : "medium",
                                                                        color: g.fulfilledByUser ? "text.secondary" : "text.primary",
                                                                    }}
                                                                >
                                                                    {g.name}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: "right", py: 2 }}>
                                                            {!g.location ? (
                                                                <Button
                                                                    onClick={() => {
                                                                        setId(g.id);
                                                                    }}
                                                                    variant="contained"
                                                                    size="small"
                                                                    sx={{ borderRadius: 2, px: 3 }}
                                                                >
                                                                    Log Guest
                                                                </Button>
                                                            ) : (
                                                                <Chip
                                                                    icon={<CheckCircleIcon />}
                                                                    label={`Logged at ${g.location.name}`}
                                                                    color="success"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: "medium" }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </>
                                        ))}
                                    </>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ textAlign: "center", py: 6 }}>
                                            <Typography variant="h6" color="text.secondary">
                                                {search.trim()
                                                    ? `No guests match "${search.trim()}".`
                                                    : "No guest lists available today."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
            <Dialog open={!!id} onClose={() => setId(undefined)} fullWidth maxWidth="sm">
                <form onSubmit={logGuest}>
                    <DialogTitle sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationOnIcon />
                            Log Guest Location
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            required
                            label="Location"
                            onChange={(e) => setSelectedLocation(parseInt(e.target.value, 10))}
                            value={selectedLocation}
                            fullWidth
                            select
                            sx={{ mt: 1 }}
                        >
                            {locations.map((l) => (
                                <MenuItem key={l.id} value={l.id}>
                                    {l.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setId(undefined)} type="button">
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" type="submit">
                            Confirm Location
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
});

export default GuestListView;
